import { Show, createSignal } from "solid-js"
import { useData } from "../context"
import { useI18n } from "../context/i18n"

// Decide C1 (docs/decide.md, rule 6): a shell command approved by the
// classifier instead of the user says so, on the tool call itself, with a
// one-click way to turn the feature off. The approval is read from the part's
// metadata (`autoApproved: {by: "decide", backend, p}`), written by the
// sidecar's shell tool on every metadata update.

export type AutoApproval = { backend: string; p: number }

/** The approval recorded on a tool part, or undefined. Validated, not trusted:
 *  metadata is a free-form record any tool can write. */
export function autoApprovalOf(metadata: Record<string, unknown> | undefined): AutoApproval | undefined {
  const value = metadata?.["autoApproved"]
  if (!value || typeof value !== "object") return
  const record = value as Record<string, unknown>
  if (record["by"] !== "decide") return
  return {
    backend: typeof record["backend"] === "string" ? record["backend"] : "",
    p: typeof record["p"] === "number" && Number.isFinite(record["p"]) ? record["p"] : Number.NaN,
  }
}

/**
 * `action` adds the «выключить» button — only where a click cannot also land on
 * a collapsible trigger around it (the tool body, not its header row).
 */
export function AutoApprovedMark(props: { approval: AutoApproval; action?: boolean }) {
  const i18n = useI18n()
  const data = useData()
  const [off, setOff] = createSignal(false)
  const [busy, setBusy] = createSignal(false)
  const detail = () =>
    i18n.t("ui.tool.autoApproved.detail", {
      backend: props.approval.backend || "—",
      p: Number.isNaN(props.approval.p) ? "—" : props.approval.p.toFixed(2),
    })

  const disable = async (event: MouseEvent) => {
    event.stopPropagation()
    if (busy() || off() || !data.disableAutoApprove) return
    setBusy(true)
    try {
      await data.disableAutoApprove()
      setOff(true)
    } finally {
      setBusy(false)
    }
  }

  return (
    <span data-component="auto-approved" data-off={off() ? "" : undefined} title={detail()}>
      <span data-slot="auto-approved-text">
        {off() ? i18n.t("ui.tool.autoApproved.off") : i18n.t("ui.tool.autoApproved")}
      </span>
      <Show when={props.action && !off() && data.disableAutoApprove}>
        <button type="button" data-slot="auto-approved-disable" disabled={busy()} onClick={disable}>
          {i18n.t("ui.tool.autoApproved.disable")}
        </button>
      </Show>
    </span>
  )
}
